
import React, { useState, useRef } from "react";
import { Upload, FileText, X } from "lucide-react";
import { cn } from "./ui/Button"; // Re-use cn

interface FileUploadProps {
  onFileSelect: (file: File | null) => void;
  selectedFile: File | null;
}

export function FileUpload({ onFileSelect, selectedFile }: FileUploadProps) {
  const [isDragOver, setIsDragOver] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      if (file.type !== "application/pdf") {
         alert("Please upload a PDF file.");
         return;
      }
      if (file.size > 1024 * 1024) {
         alert("File size exceeds 1MB. Please upload a smaller file.");
         return;
      }
      onFileSelect(file);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
        const file = e.target.files[0];
        if (file.type !== "application/pdf") {
            alert("Please upload a PDF file.");
            return;
        }
        if (file.size > 1024 * 1024) {
            alert("File size exceeds 1MB. Please upload a smaller file.");
            return;
        }
       onFileSelect(file);
    }
  };

  return (
    <div className="w-full">
      <label className="block text-sm font-medium text-slate-700 mb-2">
        Upload Resume (PDF)
      </label>
      
      {!selectedFile ? (
        <div
          onClick={() => inputRef.current?.click()}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          className={cn(
            "border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors",
            isDragOver ? "border-slate-900 bg-slate-50" : "border-slate-300 hover:border-slate-400"
          )}
        >
          <input
            type="file"
            ref={inputRef}
            className="hidden"
            accept=".pdf"
            onChange={handleChange}
          />
          <div className="flex flex-col items-center gap-2">
            <Upload className="h-8 w-8 text-slate-400" />
            <p className="text-sm text-slate-600">
              <span className="font-semibold text-slate-900">Click to upload</span> or drag and drop
            </p>
            <p className="text-xs text-slate-500">PDF only (max 1MB)</p>
          </div>
        </div>
      ) : (
        <div className="flex items-center justify-between p-4 border rounded-lg bg-slate-50 border-slate-200">
          <div className="flex items-center gap-3">
             <div className="bg-red-100 p-2 rounded">
                <FileText className="h-6 w-6 text-red-600" />
             </div>
             <div>
               <p className="text-sm font-medium text-slate-900">{selectedFile.name}</p>
               <p className="text-xs text-slate-500">{(selectedFile.size / 1024 / 1024).toFixed(2)} MB</p>
             </div>
          </div>
          <button
            onClick={() => onFileSelect(null)}
            className="text-slate-400 hover:text-slate-600 p-1"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
      )}
    </div>
  );
}
